import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import CircularProgress from "@mui/material/CircularProgress";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import InvoicesApi from "api/invoices";
import { useParams } from "react-router-dom";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import html2pdf from "html2pdf.js";
import CompanyApi from "api/companyInfo";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import numberToWords from "utils/numberToWords";
import numberToWordsArabic from "utils/numberToWordsArabic";
import numberToWordsEnglish from "utils/numberToWordEnglish";

function DeliveryNoteDetails() {
  const { id } = useParams();
  const [deliveryNoteDetails, setDeliveryNoteDetails] = useState(null);
  const [companyInfos, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { t, i18n } = useTranslation();

  const manager = localStorage.getItem("manager");

  const order = deliveryNoteDetails?.order;

  useEffect(() => {
    const fetchDeliveryNoteDetails = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setError(new Error("Token not found."));
        setLoading(false);
        return;
      }

      try {
        const response = await InvoicesApi.GetDeliveryNotesById(token, id);
        setDeliveryNoteDetails(response.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    const fetchCompanyInfos = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setError(new Error("Token not found."));
        setLoading(false);
        return;
      }

      try {
        const response = await CompanyApi.GetCompanyInfos(token);
        setCompanyInfo(response.data.results[0]);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDeliveryNoteDetails();
    fetchCompanyInfos();
  }, [id]);

  if (loading)
    return (
      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress sx={{ color: "#1F51FF" }} />
      </SoftBox>
    );

  if (error)
    return (
      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <SoftTypography variant="body1" color="error">
          {t("Erreur")}: {error.message}
        </SoftTypography>
      </SoftBox>
    );

  // eslint-disable-next-line
  const invoiceDate =
    new Date(deliveryNoteDetails?.date_created).toLocaleDateString() || "N/A";

  const downloadInvoice = (name) => {
    const element = document.getElementById("invoice-content");

    const options = {
      margin: [0.2, 0.2, 0.2, 0.2],
      filename: `${name}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      pagebreak: { mode: "avoid-all" },
    };

    // Apply RTL styles if the language is Arabic
    if (i18n.language === "ar") {
      element.style.direction = "rtl";
      element.style.textAlign = "ltr";
    }

    html2pdf().from(element).set(options).save();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox>
        <Grid container justifyContent="center">
          <Grid item xs={12} xl={10}>
            <Card
              id="invoice-content"
              sx={{
                fontFamily: "sans-serif",
                mx: { xs: 1, sm: 2, md: 3 },
                my: { xs: 1, sm: 2, md: 3 },
              }}
            >
              {/* Header Section */}
              <SoftBox
                px={{ xs: 1.5, sm: 2, md: 3 }}
                mt={{ xs: 1, sm: 2, md: 3 }}
              >
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  {/* Company Info */}
                  <Grid item xs={12} md={4}>
                    <SoftBox
                      display="flex"
                      flexDirection="column"
                      mb={{ xs: 2, md: 0 }}
                    >
                      <SoftTypography
                        variant="h5"
                        fontWeight="medium"
                        mb={1}
                        sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
                      >
                        {companyInfos?.company_name}
                      </SoftTypography>
                      <SoftBox display="flex" flexDirection="column" gap={0.5}>
                        <SoftTypography
                          variant="subtitle2"
                          fontWeight="medium"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {companyInfos?.address}
                        </SoftTypography>
                        <SoftTypography
                          variant="subtitle2"
                          fontWeight="medium"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {t("Téléphone")}: {companyInfos?.contact_phone}
                        </SoftTypography>
                        <SoftTypography
                          variant="subtitle2"
                          fontWeight="medium"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {t("Email")}: {companyInfos?.contact_email}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </Grid>

                  {/* Delivery Note Number */}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-start", md: "center" },
                      alignItems: "center",
                      mb: { xs: 2, md: 0 },
                    }}
                  >
                    <SoftBox display="flex" gap={1}>
                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="medium"
                        sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                      >
                        {t("Bon de livraison")}:
                      </SoftTypography>
                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="medium"
                        sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                      >
                        {deliveryNoteDetails?.number || ""}
                      </SoftTypography>
                    </SoftBox>
                  </Grid>

                  {/* Fiscal Info */}
                  <Grid item xs={12} md={4}>
                    <SoftBox
                      display="flex"
                      flexDirection="column"
                      alignItems={{ xs: "flex-start", md: "flex-end" }}
                    >
                      <SoftTypography
                        variant="subtitle2"
                        fontWeight="medium"
                        mb={0.5}
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        {t("Numéro fiscal")}: {companyInfos?.code_tva}
                      </SoftTypography>
                      <SoftTypography
                        variant="subtitle2"
                        fontWeight="medium"
                        mb={0.5}
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        {t("Registre de commerce")}:{" "}
                        {companyInfos?.business_registration_number}
                      </SoftTypography>
                      <SoftTypography
                        variant="subtitle2"
                        fontWeight="medium"
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        {t("Banque")}: {companyInfos?.bank_id_statement}
                      </SoftTypography>
                    </SoftBox>
                  </Grid>
                </Grid>
              </SoftBox>

              {/* Client Info */}
              <SoftBox
                display="flex"
                width="100%"
                px={{ xs: 1.5, sm: 2, md: 3 }}
                pt={{ xs: 2, sm: 3 }}
              >
                <SoftBox
                  sx={{
                    width: "100%",
                    border: "2px solid black",
                    borderBottom: "1px solid black",
                    borderRadius: 2,
                    padding: { xs: 1, sm: 1.5, md: 2 },
                  }}
                >
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                            xl: "1rem",
                          },
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("Sous la responsabilité de M.")}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                            xl: "1rem",
                          },
                          fontWeight: "medium",
                        }}
                      >
                        {deliveryNoteDetails?.client}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                            xl: "1rem",
                          },
                          fontWeight: "medium",
                        }}
                      >
                        {deliveryNoteDetails?.order?.customer}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1,
                        justifyContent: { xs: "flex-start", sm: "flex-end" },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                            xl: "1rem",
                          },
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("Utilisateur")}:
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                            xl: "1rem",
                          },
                          fontWeight: "medium",
                        }}
                      >
                        {manager}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={{ xs: 1, sm: 2 }} mt={{ xs: 0.4 }}>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: { xs: 0.5 },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                            xl: "1rem",
                          },
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("Adresse")}:
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                            xl: "1rem",
                          },
                          fontWeight: "medium",
                          wordBreak: "break-word",
                        }}
                      >
                        {deliveryNoteDetails?.address_detail}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: { xs: 0.5, sm: 2 },
                        justifyContent: { xs: "flex-start", sm: "flex-end" },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                            xl: "1rem",
                          },
                          whiteSpace: "nowrap",
                        }}
                      >
                        {companyInfos?.city}, {t("le")} {invoiceDate}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                            xl: "1rem",
                          },
                        }}
                      >
                        {t("à")} 13:47
                      </Typography>
                    </Grid>
                  </Grid>
                </SoftBox>
              </SoftBox>

              {/* Main Table Section */}
              <SoftBox px={{ xs: 1.5, sm: 2, md: 3 }} mt={2}>
                <SoftBox
                  sx={{
                    border: "2px solid black",
                    borderRadius: 2,
                    width: "100%",
                    overflowX: "auto",
                  }}
                >
                  <Table sx={{ minWidth: { xs: "500px", md: "100%" } }}>
                    <SoftBox
                      component="thead"
                      sx={{
                        width: "100%",
                        borderBottom: "2px solid black",
                      }}
                    >
                      <TableRow>
                        <SoftBox
                          component="th"
                          width="15%"
                          py={1.5}
                          px={2}
                          textAlign="center"
                          sx={{
                            borderRight: "1px solid black",
                            fontSize: { xs: "0.7rem", sm: "0.875rem" },
                          }}
                        >
                          <SoftTypography
                            variant="subtitle2"
                            fontWeight="medium"
                          >
                            {t("Code Article")}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          component="th"
                          width="45%"
                          py={1.5}
                          px={2}
                          textAlign="center"
                          sx={{
                            borderRight: "1px solid black",
                            fontSize: { xs: "0.7rem", sm: "0.875rem" },
                          }}
                        >
                          <SoftTypography
                            variant="subtitle2"
                            fontWeight="medium"
                          >
                            {t("Description")}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          component="th"
                          width="15%"
                          py={1.5}
                          px={2}
                          textAlign="center"
                          sx={{
                            borderRight: "1px solid black",
                            fontSize: { xs: "0.7rem", sm: "0.875rem" },
                          }}
                        >
                          <SoftTypography
                            variant="subtitle2"
                            fontWeight="medium"
                          >
                            {t("Quantité")}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          component="th"
                          width="15%"
                          py={1.5}
                          px={2}
                          textAlign="center"
                          sx={{
                            borderRight: "1px solid black",
                            fontSize: { xs: "0.7rem", sm: "0.875rem" },
                          }}
                        >
                          <SoftTypography
                            variant="subtitle2"
                            fontWeight="medium"
                          >
                            {t("Prix unitaire")}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          component="th"
                          width="15%"
                          py={1.5}
                          px={2}
                          textAlign="center"
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                        >
                          <SoftTypography
                            variant="subtitle2"
                            fontWeight="medium"
                          >
                            {t("Total")}
                          </SoftTypography>
                        </SoftBox>
                      </TableRow>
                    </SoftBox>
                    <TableBody>
                      {order?.order_lines?.map((line, index) => (
                        <TableRow key={`${order?.id}-${index}`}>
                          <SoftBox
                            component="td"
                            textAlign="center"
                            py={1.5}
                            px={2}
                            sx={{
                              borderRight: "1px solid black",
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            <SoftTypography variant="body2">
                              {line?.product_id}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox
                            component="td"
                            textAlign={
                              i18n.language === "ar" ? "right" : "left"
                            }
                            py={1.5}
                            px={2}
                            sx={{
                              borderRight: "1px solid black",
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            <SoftTypography variant="body2">
                              {line?.product_name}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox
                            component="td"
                            textAlign="center"
                            py={1.5}
                            px={2}
                            sx={{
                              borderRight: "1px solid black",
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            <SoftTypography variant="body2">
                              {line?.quantity || 0}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox
                            component="td"
                            textAlign="center"
                            py={1.5}
                            px={2}
                            sx={{
                              borderRight: "1px solid black",
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            <SoftTypography variant="body2">
                              {line?.unit_price
                                ? Number(line?.unit_price).toFixed(3)
                                : "0.000"}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox
                            component="td"
                            textAlign="center"
                            py={1.5}
                            px={2}
                            sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                          >
                            <SoftTypography variant="body2">
                              {line?.total_price
                                ? Number(line?.total_price).toFixed(3)
                                : "0.000"}
                            </SoftTypography>
                          </SoftBox>
                        </TableRow>
                      ))}
                      {[...Array(5)].map((_, index) => (
                        <TableRow key={`empty-${index}`}>
                          {[...Array(5)].map((_, cellIndex) => (
                            <SoftBox
                              key={`empty-cell-${cellIndex}`}
                              component="td"
                              py={1.5}
                              px={2}
                              sx={{
                                borderRight:
                                  cellIndex < 4 ? "1px solid black" : "none",
                              }}
                            >
                              <SoftTypography variant="body2">
                                &nbsp;
                              </SoftTypography>
                            </SoftBox>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </SoftBox>
              </SoftBox>

              {/* Footer Section */}
              <SoftBox px={{ xs: 1.5, sm: 2, md: 3 }} py={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.875rem" },
                    mb: 2,
                  }}
                >
                  {t("Timbre fiscal, Article 124, Loi 53/93")}: 1.000
                </Typography>

                <Grid container spacing={2}>
                  {/* Totals Table */}
                  <Grid item xs={12} md={6} xl={4}>
                    <SoftBox
                      sx={{
                        border: "2px solid black",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <Table>
                        <TableBody>
                          {[
                            {
                              label: t("Total HT"),
                              value: Number(order?.total_price).toFixed(3),
                            },
                            {
                              label: t("Total TTC"),
                              value: (
                                Number(order?.total_price) +
                                Number(companyInfos?.stamp_value)
                              ).toFixed(3),
                            },
                            {
                              label: t("Acompte fiscal"),
                              value: "0.000",
                            },
                            {
                              label: t("Montant net à payer"),
                              value: (
                                Number(order?.total_price) +
                                Number(companyInfos?.stamp_value)
                              ).toFixed(3),
                              isTotal: true,
                            },
                          ].map((row, index) => (
                            <TableRow
                              key={row.label}
                              sx={
                                row.isTotal
                                  ? { borderTop: "2px solid black" }
                                  : {}
                              }
                            >
                              <SoftBox
                                component="td"
                                px={1.5}
                                py={1}
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                }}
                              >
                                <SoftTypography variant="subtitle2">
                                  {row.label}
                                </SoftTypography>
                              </SoftBox>
                              <SoftBox
                                component="td"
                                px={1.5}
                                py={1}
                                sx={{
                                  borderLeft: "2px solid black",
                                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                }}
                              >
                                <SoftTypography variant="subtitle2">
                                  {row.value}
                                </SoftTypography>
                              </SoftBox>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </SoftBox>
                  </Grid>

                  {/* Amount in Words */}
                  <Grid item xs={12}>
                    <SoftBox
                      p={2}
                      sx={{
                        border: "2px solid black",
                        borderRadius: 2,
                        mt: { xs: 2, md: 3 },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.875rem",
                            md: "1rem",
                            xl: "1.2rem",
                          },
                          lineHeight: 1.5,
                        }}
                      >
                        {t("Montant net en toutes lettres")}:
                        {i18n.language === "fr"
                          ? numberToWords(
                              (
                                Number(order?.total_price) +
                                Number(companyInfos?.stamp_value)
                              ).toFixed(3)
                            )
                          : i18n.language === "en"
                          ? numberToWordsEnglish(
                              (
                                Number(order?.total_price) +
                                Number(companyInfos?.stamp_value)
                              ).toFixed(3)
                            )
                          : numberToWordsArabic(
                              (
                                Number(order?.total_price) +
                                Number(companyInfos?.stamp_value)
                              ).toFixed(3)
                            )}
                      </Typography>
                    </SoftBox>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>

            {/* Download Buttons */}
            <SoftBox
              p={{ xs: 1.5, sm: 2, md: 3 }}
              display="flex"
              justifyContent="flex-end"
            >
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item xs={12} sm={12} md="auto">
                  <SoftBox
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                  >
                    {/* <SoftButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      onClick={() => {
                        i18n.changeLanguage("ar");
                        downloadInvoice(deliveryNoteDetails?.number);
                      }}
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("Télécharger la facture en arabe")}
                    </SoftButton> */}
                    <SoftButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      onClick={() =>
                        downloadInvoice(deliveryNoteDetails?.number)
                      }
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {t("Télécharger")}
                    </SoftButton>
                  </SoftBox>
                </Grid>
              </Grid>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default DeliveryNoteDetails;
